import { trans } from "@mongez/localization";
import { ShopProduct } from "core/catalog";
import { useMemo } from "react";
import { BaseLink, OpenEye } from "~/design-system/components";
import { URLS } from "~/shared/urls";
import { AddToCartButton } from "./AddToCartButton";
import { Badge } from "./Badge";
import { Price } from "./Price";

type ProductCardProps = {
  product: ShopProduct;
};

export function ProductCard({ product }: ProductCardProps) {
  const customOptions = product.customOptions;

  const hasRequiredOptions = useMemo(() => {
    return customOptions?.some((option) => option.required);
  }, [customOptions]);

  return (
    <div className="hover:bg-primary-light/40 group relative flex cursor-pointer flex-col gap-4 rounded-3xl border bg-white p-3 transition-colors duration-300">
      {/* Badge */}
      <Badge product={product} />
      <div className="relative h-[220px] overflow-hidden rounded-2xl border bg-gray-100 md:h-[350px]">
        <BaseLink to={URLS.ourCakes.view(product)}>
          <img
            src={product.primaryImage?.large?.url}
            alt={product.name}
            width={360}
            height={430}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
          <span className="absolute top-0 left-0 z-10 h-full w-full" />
        </BaseLink>
        {/* If the product has required custom options, clicking the add-to-cart button should link to the product details page
        where the user can select the necessary options. Otherwise, show the standard AddToCartButton for direct add-to-cart action. */}
        {hasRequiredOptions ? (
          <BaseLink
            to={URLS.ourCakes.view(product)}
            title={trans("viewProduct")}
            aria-label={trans("viewProduct")}
            className="hover:bg-primary-light absolute right-2 bottom-2 z-20 items-center justify-center rounded-xl border bg-white p-2"
          >
            <OpenEye className="size-7" />
          </BaseLink>
        ) : (
          <AddToCartButton product={product} />
        )}
      </div>
      <BaseLink
        to={URLS.ourCakes.view(product)}
        className="flex min-h-[100px] flex-col items-center justify-center gap-2 text-center"
      >
        <h3 className="text-lg font-bold lg:text-xl">{product.name}</h3>
        <Price
          originalPrice={product.price.original}
          salePrice={product.price.sale}
        />
      </BaseLink>
    </div>
  );
}
